import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios, { type AxiosError } from "axios";
import { useSession } from "@/services/auth";

const API_URL = import.meta.env.VITE_API_URL;

interface MarkDroppedOffResponse {
  data: {
    id: string;
    status: string;
    /** false when the shipment was already past LABEL_READY — server returns
     *  the current state without touching it. UI invalidates the orders
     *  query either way so the row drops off the dashboard. */
    changed: boolean;
  };
}

/**
 * Customer "I dropped this off" self-attest.
 *
 * Server flips LABEL_READY → DROPPED_AT_CARRIER (and writes a tracking
 * event) when no active pickup booking exists for the shipment. Idempotent
 * if anything else moved the shipment forward first (carrier webhook,
 * admin scan, prior click).
 */
export function useMarkShipmentDroppedOff(options?: {
  onSuccess?: (data: MarkDroppedOffResponse) => void;
  onError?: (error: unknown) => void;
}) {
  const queryClient = useQueryClient();
  const { token, customer } = useSession();

  return useMutation({
    mutationFn: async ({ shipmentId }: { shipmentId: string }) => {
      const { data } = await axios.post<MarkDroppedOffResponse>(
        `${API_URL}/api/v2/shipments/${shipmentId}/dropped-off`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: ["customerOrdersV2", customer?.id],
      });
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}

export function isPickupBookingActiveError(err: unknown): err is AxiosError<{
  error: "PICKUP_BOOKING_ACTIVE";
  message?: string;
}> {
  return (
    axios.isAxiosError(err) &&
    err.response?.status === 409 &&
    (err.response.data as { error?: string })?.error === "PICKUP_BOOKING_ACTIVE"
  );
}
